// Unified Search Service - Manages search history and filter presets across all management sections

const SEARCH_HISTORY_KEY = 'arddjo_search_history';
const FILTER_PRESETS_KEY = 'arddjo_filter_presets';
const MAX_HISTORY_ITEMS = 20;

// Search sections configuration
export const SEARCH_SECTIONS = {
  properties: {
    id: 'properties',
    label: 'العقارات',
    icon: 'Building2',
    route: '/property-management-dashboard',
    searchFields: ['name', 'owner', 'address', 'type', 'status']
  },
  clients: {
    id: 'clients',
    label: 'العملاء',
    icon: 'Users',
    route: '/client-relationship-management',
    searchFields: ['name', 'category', 'contact', 'priority']
  },
  contracts: {
    id: 'contracts',
    label: 'العقود',
    icon: 'FileText',
    route: '/contract-approval-workflow',
    searchFields: ['contractNumber', 'clientName', 'status', 'priority', 'stage']
  },
  staff: {
    id: 'staff',
    label: 'الموظفين',
    icon: 'UserCog',
    route: '/system-administration-and-user-management',
    searchFields: ['name', 'role', 'department', 'status']
  }
};

// Get search history from localStorage
export const getSearchHistory = () => {
  try {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error loading search history:', error);
    return [];
  }
};

// Add search to history
export const addToSearchHistory = (searchTerm, section) => {
  try {
    const history = getSearchHistory();
    const newEntry = {
      id: Date.now(),
      term: searchTerm,
      section: section,
      timestamp: new Date()?.toISOString()
    };

    // Remove duplicate searches
    const filteredHistory = history?.filter(
      item => !(item?.term === searchTerm && item?.section === section)
    );

    // Add new entry at the beginning
    const updatedHistory = [newEntry, ...filteredHistory]?.slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    return updatedHistory;
  } catch (error) {
    console.error('Error adding to search history:', error);
    return [];
  }
};

// Clear search history
export const clearSearchHistory = () => {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing search history:', error);
    return false;
  }
};

// Remove specific search from history
export const removeFromSearchHistory = (id) => {
  try {
    const history = getSearchHistory();
    const updatedHistory = history?.filter(item => item?.id !== id);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
    return updatedHistory;
  } catch (error) {
    console.error('Error removing from search history:', error);
    return [];
  }
};

// Get filter presets from localStorage
export const getFilterPresets = () => {
  try {
    const presets = localStorage.getItem(FILTER_PRESETS_KEY);
    return presets ? JSON.parse(presets) : getDefaultPresets();
  } catch (error) {
    console.error('Error loading filter presets:', error);
    return getDefaultPresets();
  }
};

// Default filter presets
const getDefaultPresets = () => [
  {
    id: 'available_properties',
    name: 'العقارات المتاحة',
    section: 'properties',
    filters: { status: 'متاح', propertyType: 'all' },
    icon: 'CheckCircle2'
  },
  {
    id: 'high_value',
    name: 'العقارات عالية القيمة',
    section: 'properties',
    filters: { propertyType: 'all', status: 'all' },
    icon: 'TrendingUp'
  },
  {
    id: 'active_clients',
    name: 'العملاء النشطون',
    section: 'clients',
    filters: { category: 'active' },
    icon: 'UserCheck'
  },
  {
    id: 'pending_contracts',
    name: 'العقود قيد المراجعة',
    section: 'contracts',
    filters: { status: 'pending_review' },
    icon: 'Clock'
  },
  {
    id: 'urgent_contracts',
    name: 'العقود العاجلة',
    section: 'contracts',
    filters: { priority: 'urgent' },
    icon: 'AlertCircle'
  },
  {
    id: 'admin_users',
    name: 'المسؤولون',
    section: 'staff',
    filters: { role: 'admin' },
    icon: 'Shield'
  }
];

// Save filter preset
export const saveFilterPreset = (name, section, filters) => {
  try {
    const presets = getFilterPresets();
    const newPreset = {
      id: `custom_${Date.now()}`,
      name,
      section,
      filters,
      icon: 'Star',
      isCustom: true,
      timestamp: new Date()?.toISOString()
    };

    const updatedPresets = [...presets, newPreset];
    localStorage.setItem(FILTER_PRESETS_KEY, JSON.stringify(updatedPresets));
    return updatedPresets;
  } catch (error) {
    console.error('Error saving filter preset:', error);
    return [];
  }
};

// Delete filter preset
export const deleteFilterPreset = (id) => {
  try {
    const presets = getFilterPresets();
    const updatedPresets = presets?.filter(preset => preset?.id !== id);
    localStorage.setItem(FILTER_PRESETS_KEY, JSON.stringify(updatedPresets));
    return updatedPresets;
  } catch (error) {
    console.error('Error deleting filter preset:', error);
    return [];
  }
};

// Get presets for specific section
export const getPresetsForSection = (section) => {
  const allPresets = getFilterPresets();
  return allPresets?.filter(preset => preset?.section === section);
};

export default {
  getSearchHistory,
  addToSearchHistory,
  clearSearchHistory,
  removeFromSearchHistory,
  getFilterPresets,
  saveFilterPreset,
  deleteFilterPreset,
  getPresetsForSection,
  SEARCH_SECTIONS
};